import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import toast from "react-hot-toast";
import { FiUsers, FiAlertTriangle, FiActivity, FiArrowRight, FiCheckCircle, FiSearch, FiCpu } from "react-icons/fi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { motion, AnimatePresence } from "framer-motion";

const AIAnalytics = () => {
  // Selector states
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("risk"); // risk or anomalies

  // Loaded data
  const [riskData, setRiskData] = useState([]);
  const [anomaliesData, setAnomaliesData] = useState([]);
  
  // Drill-down Modal
  const [showModal, setShowModal] = useState(false);
  const [modalStudent, setModalStudent] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalAiReport, setModalAiReport] = useState(null);

  const [sections, setSections] = useState(["A", "B", "C", "D"]);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        let params = {};
        if (selectedBranch) params.branch = selectedBranch;
        if (selectedSemester) params.semester = selectedSemester;
        const res = await axios.get(`${baseApiURL()}/section/getSectionsByBranchAndSemester`, { params });
        if (res.data.success && res.data.sections?.length > 0) {
          setSections(res.data.sections);
        }
      } catch (err) {
        console.error("Error fetching dynamic sections:", err);
      }
    };
    fetchSections();
  }, [selectedBranch, selectedSemester]);

  // Fetch branches on mount
  useEffect(() => {
    axios
      .get(`${baseApiURL()}/branch/getBranch`)
      .then((res) => {
        if (res.data.success) {
          setBranches(res.data.branches);
        }
      })
      .catch((err) => console.error("Error loading branches:", err));
  }, []);

  const handleFetchAnalytics = async () => {
    if (!selectedBranch || !selectedSemester || !selectedSection) {
      toast.error("Please select branch, semester, and section.");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // 1. Fetch Section Risk Summary
      const riskRes = await axios.get(`${baseApiURL()}/ai/section-risk`, {
        params: { branch: selectedBranch, semester: selectedSemester, section: selectedSection },
        headers
      });

      // 2. Fetch Section Anomalies
      const anomalyRes = await axios.get(`${baseApiURL()}/ai/anomalies-section`, {
        params: { branch: selectedBranch, semester: selectedSemester, section: selectedSection },
        headers
      });

      if (riskRes.data.success && anomalyRes.data.success) {
        setRiskData(riskRes.data.summary);
        setAnomaliesData(anomalyRes.data.anomalies);
        toast.success("AI Analytics loaded successfully!");
      } else {
        toast.error("Failed to load some analytics components.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error fetching analytics details from server.");
    } finally {
      setLoading(false);
    }
  };

  // Detailed AI Student Risk report
  const handleOpenDetailedAnalysis = async (student) => {
    setModalStudent(student);
    setModalAiReport(null);
    setShowModal(true);
    setModalLoading(true);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const res = await axios.get(`${baseApiURL()}/ai/risk/${student.enrollmentNo}`, { headers });
      if (res.data.success) {
        setModalAiReport(res.data.data);
      } else {
        toast.error("Failed to generate detailed analysis.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error generating detailed AI risk prediction.");
    } finally {
      setModalLoading(false);
    }
  };

  // Stats Calculations
  const totalStudents = riskData.length;
  const highRiskCount = riskData.filter(s => s.riskLevel === "High").length;
  const mediumRiskCount = riskData.filter(s => s.riskLevel === "Medium").length;
  const totalAnomalies = anomaliesData.length;

  // Chart Data preparation
  const riskChartData = [
    { name: "High Risk", value: highRiskCount, color: "#EF4444" },
    { name: "Medium Risk", value: mediumRiskCount, color: "#F59E0B" },
    { name: "Low Risk", value: totalStudents - highRiskCount - mediumRiskCount, color: "#10B981" }
  ].filter(c => c.value > 0);

  // Group anomalies by type
  const anomalyCounts = anomaliesData.reduce((acc, curr) => {
    acc[curr.anomalyType] = (acc[curr.anomalyType] || 0) + 1;
    return acc;
  }, {});

  const anomalyChartData = Object.keys(anomalyCounts).map(key => ({
    name: key,
    count: anomalyCounts[key]
  }));

  return (
    <div className="space-y-6">
      
      {/* Page Header */}
      <div className="bento-header-banner flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <FiActivity className="text-indigo-600 dark:text-indigo-400" />
            AI Student Risk & Attendance Analytics
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
            Detect patterns, flag attendance anomalies, and predict academic performance risks.
          </p>
        </div>
      </div>

      {/* Selectors Panel */}
      <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs space-y-4">
        <h3 className="font-bold text-slate-700 dark:text-slate-300 text-xs uppercase tracking-wider">Configure Section Target</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Branch</label>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3.5 py-2.5 text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
            >
              <option value="">-- Select Branch --</option>
              {branches.map((b) => (
                <option key={b._id} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Semester</label>
            <select
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3.5 py-2.5 text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
            >
              <option value="">-- Select Semester --</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                <option key={sem} value={sem}>
                  Semester {sem}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Section</label>
            <div className="flex gap-2">
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="flex-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3.5 py-2.5 text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
              >
                <option value="">-- Select Section --</option>
                {sections.map((s) => (
                  <option key={s} value={s}>
                    Section {s}
                  </option>
                ))}
              </select>
              <button
                onClick={handleFetchAnalytics}
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold px-5 rounded-xl transition-all shadow-xs active:scale-95 disabled:opacity-50 flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {loading ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <FiSearch /> Load
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>

      {riskData.length > 0 ? (
        <div className="space-y-6">
          
          {/* Stats Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs flex items-center gap-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 rounded-xl border border-blue-100 dark:border-blue-800">
                <FiUsers className="w-5 h-5" />
              </div>
              <div>
                <p className="text-slate-400 dark:text-slate-500 text-[10px] font-bold uppercase tracking-wider">Students Monitored</p>
                <p className="text-2xl font-black text-slate-900 dark:text-white">{totalStudents}</p>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs flex items-center gap-4">
              <div className="p-3 bg-red-50 dark:bg-red-950/60 text-red-600 dark:text-red-400 rounded-xl border border-red-100 dark:border-red-800">
                <FiAlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <p className="text-slate-400 dark:text-slate-500 text-[10px] font-bold uppercase tracking-wider">High Risk Predicted</p>
                <p className="text-2xl font-black text-red-600 dark:text-red-400">{highRiskCount}</p>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs flex items-center gap-4">
              <div className="p-3 bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 rounded-xl border border-amber-100 dark:border-amber-800">
                <FiAlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <p className="text-slate-400 dark:text-slate-500 text-[10px] font-bold uppercase tracking-wider">Medium Risk</p>
                <p className="text-2xl font-black text-amber-600 dark:text-amber-400">{mediumRiskCount}</p>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs flex items-center gap-4">
              <div className="p-3 bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 rounded-xl border border-purple-100 dark:border-purple-800">
                <FiActivity className="w-5 h-5" />
              </div>
              <div>
                <p className="text-slate-400 dark:text-slate-500 text-[10px] font-bold uppercase tracking-wider">Anomalies Detected</p>
                <p className="text-2xl font-black text-purple-600 dark:text-purple-400">{totalAnomalies}</p>
              </div>
            </div>

          </div>

          {/* Visual Recharts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Risk Breakdown Pie */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs">
              <h3 className="font-bold text-slate-900 dark:text-white mb-4 text-xs uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3">AI Risk Distribution</h3>
              <div className="h-60 flex items-center justify-center">
                {riskChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={riskChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {riskChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-slate-400 dark:text-slate-500 text-xs">No critical data to display</p>
                )}
              </div>
            </div>

            {/* Anomaly Bar Chart */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs">
              <h3 className="font-bold text-slate-900 dark:text-white mb-4 text-xs uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3">Anomaly Type Breakdown</h3>
              <div className="h-60">
                {anomalyChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={anomalyChartData} margin={{ bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 dark:text-slate-500 text-xs gap-2">
                    <FiCheckCircle className="text-emerald-500 w-5 h-5" />
                    <span>No attendance anomalies detected in this section!</span>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Detailed Lists tabs */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
            
            {/* Tab selection */}
            <div className="flex border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
              <button
                onClick={() => setActiveTab("risk")}
                className={`flex-1 py-3.5 font-bold text-xs transition border-b-2 flex items-center justify-center gap-2 cursor-pointer ${
                  activeTab === "risk"
                    ? "border-indigo-600 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-slate-900"
                    : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                <FiAlertTriangle />
                Student Academic Risk Predictions
              </button>
              <button
                onClick={() => setActiveTab("anomalies")}
                className={`flex-1 py-3.5 font-bold text-xs transition border-b-2 flex items-center justify-center gap-2 cursor-pointer ${
                  activeTab === "anomalies"
                    ? "border-indigo-600 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-slate-900"
                    : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                <FiActivity />
                Detected Attendance Anomalies ({totalAnomalies})
              </button>
            </div>

            {/* Tab content */}
            <div className="p-4 md:p-5">
              
              {activeTab === "risk" && (
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                  <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 text-left">Enrollment No</th>
                        <th className="px-4 py-3 text-left">Student Name</th>
                        <th className="px-4 py-3 text-left">Overall Attendance</th>
                        <th className="px-4 py-3 text-left">Risk Score</th>
                        <th className="px-4 py-3 text-left">Risk Level</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                      {riskData.map((std) => (
                        <tr key={std.enrollmentNo} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                          <td className="px-4 py-3.5 font-mono font-bold text-slate-900 dark:text-white">{std.enrollmentNo}</td>
                          <td className="px-4 py-3.5 font-medium">{std.name}</td>
                          <td className="px-4 py-3.5 font-bold">
                            {std.attendancePercentage !== "N/A" ? `${std.attendancePercentage}%` : "N/A"}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${
                                    std.riskLevel === "High" ? "bg-red-500" : std.riskLevel === "Medium" ? "bg-amber-500" : "bg-emerald-500"
                                  }`}
                                  style={{ width: `${std.riskScore}%` }}
                                />
                              </div>
                              <span className="text-[11px] font-mono font-bold">{std.riskScore}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span
                              className={`px-2.5 py-0.5 text-[10px] font-bold rounded-lg border ${
                                std.riskLevel === "High"
                                  ? "bg-red-50 dark:bg-red-950/60 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800"
                                  : std.riskLevel === "Medium"
                                  ? "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                                  : "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                              }`}
                            >
                              {std.riskLevel}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <button
                              onClick={() => handleOpenDetailedAnalysis(std)}
                              className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 px-3 py-1.5 rounded-lg transition cursor-pointer"
                            >
                              Analyze Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "anomalies" && (
                <div>
                  {anomaliesData.length > 0 ? (
                    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                        <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                          <tr>
                            <th className="px-4 py-3 text-left">Student Name</th>
                            <th className="px-4 py-3 text-left">Enrollment No</th>
                            <th className="px-4 py-3 text-left">Anomaly Type</th>
                            <th className="px-4 py-3 text-left">Severity</th>
                            <th className="px-4 py-3 text-left">Details</th>
                            <th className="px-4 py-3 text-left">Trigger Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                          {anomaliesData.map((anom, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                              <td className="px-4 py-3.5 font-bold text-slate-900 dark:text-white">{anom.studentName}</td>
                              <td className="px-4 py-3.5 font-mono text-slate-500 dark:text-slate-400">{anom.enrollmentNo}</td>
                              <td className="px-4 py-3.5">
                                <span className="font-semibold text-slate-700 dark:text-slate-300">{anom.anomalyType}</span>
                              </td>
                              <td className="px-4 py-3.5">
                                <span
                                  className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border ${
                                    anom.severity === "High"
                                      ? "bg-red-50 dark:bg-red-950/60 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800"
                                      : "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                                  }`}
                                >
                                  {anom.severity}
                                </span>
                              </td>
                              <td className="px-4 py-3.5 max-w-xs truncate md:max-w-md text-xs text-slate-600 dark:text-slate-400">{anom.details}</td>
                              <td className="px-4 py-3.5 text-xs text-slate-400 dark:text-slate-500 font-mono">
                                {anom.date ? new Date(anom.date).toLocaleDateString() : "Recurring Pattern"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
                      <FiCheckCircle className="w-12 h-12 text-emerald-500 mb-3" />
                      <h4 className="font-bold text-slate-700 dark:text-slate-300 text-sm">Perfect Class Section Status</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">No attendance anomalies detected in this section.</p>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>

        </div>
      ) : (
        <div className="py-20 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl flex flex-col items-center justify-center bg-white dark:bg-slate-900">
          <div className="w-14 h-14 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mb-3 border border-indigo-100 dark:border-indigo-800">
            <FiActivity className="w-7 h-7" />
          </div>
          <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm">No AI Analytics Loaded</h3>
          <p className="text-slate-400 dark:text-slate-500 text-xs mt-1 max-w-sm px-6">
            Configure target branch, semester, and section in selectors panel and click Load to run AI risk modeling.
          </p>
        </div>
      )}

      {/* Drill-down detailed analysis Modal */}
      <AnimatePresence>
        {showModal && modalStudent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-900 text-slate-100 rounded-2xl border border-slate-800 shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden"
            >
              {/* Modal Header */}
              <div className="px-6 py-4 bg-slate-950 flex items-center justify-between border-b border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-indigo-500 to-cyan-500 flex items-center justify-center text-white">
                    <FiCpu className="animate-spin" style={{ animationDuration: '6s' }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm md:text-base">AI Academic Performance Report</h3>
                    <p className="text-xs text-slate-400">Enrollment No: {modalStudent.enrollmentNo}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-slate-400 hover:text-white hover:bg-slate-800 p-1.5 rounded-lg transition-all text-sm font-semibold"
                >
                  Close
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-6">
                
                {/* Student Info Bar */}
                <div className="grid grid-cols-2 gap-4 bg-slate-950/60 p-4 rounded-xl border border-slate-800/80 text-xs">
                  <div>
                    <span className="text-slate-500 block uppercase tracking-wider font-semibold">Student Name</span>
                    <span className="text-sm font-semibold mt-0.5 block">{modalStudent.name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block uppercase tracking-wider font-semibold">Section Attendance</span>
                    <span className="text-sm font-semibold mt-0.5 block">
                      {modalStudent.attendancePercentage !== "N/A" ? `${modalStudent.attendancePercentage}%` : "N/A"}
                    </span>
                  </div>
                </div>

                {modalLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-3">
                    <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-slate-400">ECAP AI is modeling student data...</p>
                  </div>
                ) : modalAiReport ? (
                  <div className="space-y-6">
                    
                    {/* Risk Badge Block */}
                    <div className="flex items-center justify-between bg-slate-950/20 border border-slate-800/60 p-4 rounded-xl">
                      <div>
                        <span className="text-slate-400 text-xs">Risk Index Score</span>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-3xl font-extrabold text-white">{modalAiReport.riskScore}</span>
                          <span className="text-xs text-slate-500">/ 100</span>
                        </div>
                      </div>
                      <span
                        className={`px-4 py-1.5 text-xs font-black rounded-full uppercase ${
                          modalAiReport.riskLevel === "High"
                            ? "bg-red-500/20 text-red-400 border border-red-500/30"
                            : modalAiReport.riskLevel === "Medium"
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                            : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                        }`}
                      >
                        {modalAiReport.riskLevel} Risk Level
                      </span>
                    </div>

                    {/* AI Summary */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-cyan-400 mb-2">AI Summary</h4>
                      <p className="text-sm leading-relaxed text-slate-200 bg-slate-950/40 p-4 rounded-xl border border-slate-800">
                        {modalAiReport.summary}
                      </p>
                    </div>

                    {/* Contributing Factors */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-2">Contributing Factors</h4>
                      <ul className="space-y-2">
                        {modalAiReport.details?.map((detail, idx) => (
                          <li key={idx} className="flex gap-2 text-sm text-slate-300 items-start">
                            <span className="text-red-500 mt-1">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* AI Recommendations */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-2">AI Intervention Guidance</h4>
                      <ul className="space-y-2">
                        {modalAiReport.recommendations?.map((rec, idx) => (
                          <li key={idx} className="flex gap-2 text-sm text-slate-300 items-start">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                  </div>
                ) : (
                  <p className="text-center text-slate-400 text-sm">Failed to generate AI analysis report.</p>
                )}

              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-slate-950 border-t border-slate-800 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-5 py-2.5 rounded-xl transition-all active:scale-95"
                >
                  Done
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AIAnalytics;
